/**
 * Notification Center Component
 *
 * Real-time notification system with:
 * - Grant alerts & deadline reminders
 * - Sync status updates
 * - System announcements
 * - Team collaboration notifications
 */
import { useState } from 'react'
import {
  Bell,
  CheckCircle,
  AlertTriangle,
  Info,
  Calendar,
  Users,
  FileText,
  X,
  Check,
  Trash2,
  Settings,
  ExternalLink,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

// Types
export interface Notification {
  id: string
  type: 'success' | 'warning' | 'info' | 'alert'
  category: 'grant' | 'sync' | 'team' | 'system' | 'deadline'
  title: string
  message: string
  timestamp: string
  read: boolean
  actionUrl?: string
  actionLabel?: string
}

interface NotificationCenterProps {
  className?: string
}

// Mock notifications
const MOCK_NOTIFICATIONS: Notification[] = [
  {
    id: '1',
    type: 'alert',
    category: 'deadline',
    title: 'Grant Deadline Tomorrow',
    message: 'Horizon Europe EIC Pathfinder submission closes in 24 hours.',
    timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    read: false,
    actionUrl: '/sagitta/grants',
    actionLabel: 'View Grant',
  },
  {
    id: '2',
    type: 'success',
    category: 'sync',
    title: 'OpenAIRE Sync Complete',
    message: '12,500 new records indexed from OpenAIRE. 3 new grants match your profile.',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    read: false,
    actionUrl: '/cassiopeia/connectors',
    actionLabel: 'View Details',
  },
  {
    id: '3',
    type: 'info',
    category: 'team',
    title: 'New Consortium Invitation',
    message: 'TU Munich invited you to join the AI4Health consortium proposal.',
    timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    read: false,
    actionUrl: '/andromeda/partners',
    actionLabel: 'Review Invitation',
  },
  {
    id: '4',
    type: 'warning',
    category: 'sync',
    title: 'Copernicus Sync Delayed',
    message: 'Rate limit reached. Sync will resume in 4 hours.',
    timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    read: true,
    actionUrl: '/cassiopeia/connectors',
    actionLabel: 'Check Status',
  },
  {
    id: '5',
    type: 'success',
    category: 'grant',
    title: 'New Grant Match: 95% Score',
    message: 'ERC Starting Grant matches your research profile with high relevance.',
    timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    read: true,
    actionUrl: '/sagitta/grants',
    actionLabel: 'View Match',
  },
  {
    id: '6',
    type: 'info',
    category: 'system',
    title: 'Platform Update Available',
    message: 'New features: AI Proposal Generator and Partner Matching improvements.',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    read: true,
  },
]

const typeIcons = {
  success: CheckCircle,
  warning: AlertTriangle,
  info: Info,
  alert: Bell,
}

const categoryIcons = {
  grant: FileText,
  sync: ExternalLink,
  team: Users,
  system: Settings,
  deadline: Calendar,
}

const typeColors = {
  success: 'text-green-500',
  warning: 'text-yellow-500',
  info: 'text-blue-500',
  alert: 'text-red-500',
}

function formatTimeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${days}d ago`
}

export function NotificationCenter({ className }: NotificationCenterProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>(MOCK_NOTIFICATIONS)
  const [filter, setFilter] = useState<'all' | 'unread'>('all')

  const unreadCount = notifications.filter((n) => !n.read).length
  const filteredNotifications =
    filter === 'unread' ? notifications.filter((n) => !n.read) : notifications

  const markAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    )
  }

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }

  const deleteNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }

  const clearAll = () => {
    setNotifications([])
    setIsOpen(false)
  }

  return (
    <div className={cn('relative', className)}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-medium text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <Card className="absolute right-0 top-12 z-50 w-96 max-h-[80vh] overflow-hidden shadow-lg border">
            {/* Header */}
            <div className="flex items-center justify-between border-b p-4">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                <h3 className="font-semibold">Notifications</h3>
                {unreadCount > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {unreadCount} new
                  </Badge>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Filter Tabs */}
            <div className="flex border-b px-4">
              <button
                className={cn(
                  'px-3 py-2 text-sm font-medium border-b-2 transition-colors',
                  filter === 'all'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button
                className={cn(
                  'px-3 py-2 text-sm font-medium border-b-2 transition-colors',
                  filter === 'unread'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
                onClick={() => setFilter('unread')}
              >
                Unread ({unreadCount})
              </button>
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto max-h-[50vh]">
              {filteredNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Bell className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p className="text-sm text-muted-foreground">
                    {filter === 'unread'
                      ? 'No unread notifications'
                      : 'No notifications yet'}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredNotifications.map((notification) => {
                    const TypeIcon = typeIcons[notification.type]
                    const CategoryIcon = categoryIcons[notification.category]

                    return (
                      <div
                        key={notification.id}
                        className={cn(
                          'p-4 hover:bg-accent/50 transition-colors',
                          !notification.read && 'bg-accent/30'
                        )}
                      >
                        <div className="flex gap-3">
                          {/* Icon */}
                          <div
                            className={cn(
                              'flex-shrink-0 mt-0.5',
                              typeColors[notification.type]
                            )}
                          >
                            <TypeIcon className="h-5 w-5" />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p
                                className={cn(
                                  'text-sm font-medium',
                                  !notification.read && 'font-semibold'
                                )}
                              >
                                {notification.title}
                              </p>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatTimeAgo(notification.timestamp)}
                              </span>
                            </div>

                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {notification.message}
                            </p>

                            {/* Actions */}
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs gap-1">
                                <CategoryIcon className="h-3 w-3" />
                                {notification.category}
                              </Badge>

                              {notification.actionUrl && (
                                <a
                                  href={notification.actionUrl}
                                  className="text-xs text-primary hover:underline"
                                  onClick={() => {
                                    markAsRead(notification.id)
                                    setIsOpen(false)
                                  }}
                                >
                                  {notification.actionLabel || 'View'}
                                </a>
                              )}

                              <div className="flex-1" />

                              {!notification.read && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => markAsRead(notification.id)}
                                  title="Mark as read"
                                >
                                  <Check className="h-3 w-3" />
                                </Button>
                              )}

                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                onClick={() => deleteNotification(notification.id)}
                                title="Delete"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="flex items-center justify-between border-t p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  disabled={unreadCount === 0}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Mark all read
                </Button>
                <Button variant="ghost" size="sm" onClick={clearAll}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Clear all
                </Button>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  )
}
